import React, { useEffect, useState } from 'react';
import { useAppSelector } from '../../app/hooks';
import {
  selectFeatureFlagHitl3,
  selectAgentResponseTimeLimitSeconds,
  selectIsHitl7Enabled,
} from '../../redux/features/config/config.selector';
import { selectIsAIAutoMode } from '../../redux/features/ai/ai.selector';
import { selectAIIndicator } from '../../selectors/restaurant';
import { selectSessionId } from '../../redux/features/sessionBoundary/sessionBoundary.selectors';
import useAwaitingResponseAnchor from '../../hooks/useAwaitingResponseAnchor.hooks';
import PulseMeterTile from './PulseMeterTile';
import PulseDialChip from './PulseDialChip';

/**
 * RESPONSE TIME (HITL 3.0 Pulse tile).
 *
 * A per-turn SLA meter: it counts up from the customer's last utterance, and the
 * agent must send a TTS reply before the configured limit
 * (`AGENT_RESPONSE_TIME_LIMIT_SECONDS`, default 6s) elapses. The moment the agent
 * replies the tile resets to 0s / empty and waits for the next utterance
 * (see `selectAwaitingResponseSinceMs`).
 *
 * Which utterance is awaiting a reply is decided on the ORDERBOARD's own clock
 * (its customer stamp against its TTS stamp — internally consistent). HOW LONG
 * it has been awaiting is measured on the browser clock at both ends, via
 * `useAwaitingResponseAnchor` — see that hook for why (HITL-1569).
 *
 * Shown once the human has intercepted the AI (the AI is no longer in auto mode —
 * `!isAIAutoMode`, which the agent-interception action sets) OR whenever the
 * restaurant is pure HITL (no AI to intercept) — and, either way, only while a
 * session is active. It is always off unless hitl_3_0 is on.
 *
 * Re-renders once on each whole-second boundary (a self-correcting timeout aimed
 * at the next second stroke) so the numeral flips crisply on the second rather
 * than churning every few ms. The numeral counts whole seconds with no unit
 * suffix; the bars (one per limit second) step in lockstep. Over the limit it
 * stays red.
 *
 * HITL-1663 (hitl_7_0): the Copilot card becomes a chip strip, and this is the
 * only metric that survives it — Turn, Sentiment and Menu Board Time all go. So
 * under 7.0 the same numbers render as a single dial chip (PulseDialChip)
 * instead of the value+label+bar tile, to sit on one line with the signal pills.
 * When or whether the tile shows is unchanged; only its shape is.
 */

// Elapsed shown as whole seconds counting up, no unit suffix: 0, 1, 2, 3…
const secondsLabel = (ms: number): string => `${Math.floor(ms / 1000)}`;

const ResponseTimeTile = () => {
  const isHitl3 = useAppSelector(selectFeatureFlagHitl3);
  const isHitl7 = useAppSelector(selectIsHitl7Enabled);
  const limitSeconds = useAppSelector(selectAgentResponseTimeLimitSeconds);
  const isAIAutoMode = useAppSelector(selectIsAIAutoMode);
  const aiEnabled = useAppSelector(selectAIIndicator);
  const sessionId = useAppSelector(selectSessionId);

  // Shown while a session is active AND either:
  //   • the order is AI-enabled and the human has intercepted it — "agent
  //     interception has happened" = the AI is no longer driving (the
  //     agent-interception action flips isAIAutoMode to false); or
  //   • the restaurant is pure HITL — there is no AI to intercept, so the tile
  //     is relevant for the whole session.
  const agentIntercepted = !isAIAutoMode;
  const pureHITL = !aiEnabled;
  const active =
    isHitl3 &&
    !!sessionId &&
    ((aiEnabled && agentIntercepted) || pureHITL) &&
    limitSeconds > 0;

  // Browser-clock instant this utterance started awaiting a reply; null when idle.
  const anchorMs = useAwaitingResponseAnchor(active);
  const [nowMs, setNowMs] = useState(() => Date.now());

  // Re-render on each whole-second boundary while an utterance is awaiting a
  // reply; otherwise the effect stays inert so nothing runs in the background. A
  // self-correcting timeout targets the next second stroke (relative to the
  // anchor) so the numeral flips exactly on the second and never drifts.
  useEffect(() => {
    if (anchorMs == null) {
      return undefined;
    }
    setNowMs(Date.now()); // recompute immediately on a new utterance

    let timeoutId: ReturnType<typeof setTimeout>;
    const scheduleNextStroke = () => {
      // ms remaining until the elapsed time next lands on a whole second.
      const msToNextSecond = 1000 - ((Date.now() - anchorMs) % 1000);
      timeoutId = setTimeout(() => {
        setNowMs(Date.now());
        scheduleNextStroke();
      }, msToNextSecond);
    };
    scheduleNextStroke();
    return () => clearTimeout(timeoutId);
  }, [anchorMs]);

  if (!active) {
    return null;
  }

  // Both ends are the browser clock, so this is a genuine floor for the single
  // paint between a new anchor and the effect refreshing `nowMs` — not a mask
  // over two clocks disagreeing, which is what it used to be (HITL-1569).
  const elapsedMs = anchorMs != null ? Math.max(0, nowMs - anchorMs) : 0;

  // Same testId in both shapes: callers and tests ask "is the response meter
  // showing", which is a question about this tile's gate, not its chrome.
  return isHitl7 ? (
    <PulseDialChip
      label="Response"
      elapsedMs={elapsedMs}
      limitMs={limitSeconds * 1000}
      formatValue={secondsLabel}
      testId="response-time-tile"
    />
  ) : (
    <PulseMeterTile
      label="Response time"
      elapsedMs={elapsedMs}
      limitMs={limitSeconds * 1000}
      formatValue={secondsLabel}
      testId="response-time-tile"
    />
  );
};

export default ResponseTimeTile;
